import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from 'react-router-dom';
import log from "loglevel";
import "./QuizGame.css";
import PreventPullToRefresh from "./PreventPullToRefresh";
import { Modal, Button, Typography } from "@mui/material";
import QuizMindMap from "./QuizMindMap";
import QuizVdo from "./QuizVdo";
import moment from "moment";
import { saveAs } from "file-saver";



export default function QuizGame() {
    // ----- Log Level Configuration -----
    log.setLevel(process.env.REACT_APP_LOG_LEVEL || "info");

    const [openMindMap, setOpenMindMap] = useState(false);
    const [openVdo, setOpenVdo] = useState(false);

    // Parameters ใช้ useParams เพื่ออ่านพารามิเตอร์จาก URL
    const { quizFile } = useParams();
    const { quizMode } = useParams();
    // Query parameters
    const [searchParams] = useSearchParams(); // Get query parameters
    const wrong = searchParams.get("wrong");

    const wrongQuestionDict = wrong
        ? wrong.split(",")
            .reduce((acc, val) => {
                acc[val] = val; // Key = Value
                return acc;
            }, {})
        : null;


    log.info(`Quiz file: ${quizFile} mode: ${quizMode} wrong: ${wrong}`);

    // ----- Load Quiz Data -----
    /**
     * quizData (state variable) → Stores an array of quiz questions. Initially, it's an empty array [].
     * setQuizData (function) → Used to update the quizData state.
     * useState([]) → Initializes quizData with an empty array.
     **/
    const [quizData, setQuizData] = useState([]);
    const [quizNo, setQuizNo] = useState({}); // for maching Quiz No i.e. [2] with Quiz Id, example data: { 2: 2 }
    // Fetch the quiz data once the component is mounted (just first time)
    useEffect(() => {
        fetch(process.env.PUBLIC_URL + "/" + quizFile) // Ensure the file is accessible from the public folder
            .then((response) => response.text())
            .then((text) => {
                const parsedData = parseQuizData(text);
                setQuizData(parsedData);
                log.info("Quiz data loaded:", parsedData);
                log.info("Quiz no:", quizNo);
            })
            .catch((error) => log.error("Error loading the file:", error));
    }, []);

    /**
     * @param {Quiz data e.g. quiz.question, quiz.explained} data 
     * @param {int} lineNo, lineNo
     * @param {[]} lines, all lines
     * @returns int lineNo
     **/
    function buildTag(data, lineNo, lines) {
        while (lineNo < lines.length) {// access line by line until the end
            let line = lines[lineNo];
            // log.info(`Build tag line: ${lineNo} data: ${line}`);

            // condition length = 0 for prevent reading an empty line or reading till the last line
            // condition --o-- for prevent read question until the last line
            // condition --q-- for prevent read explaine until the last line
            if (line.trim().length == 0 || line.includes("--o--") || line.includes("--q--")) {
                lineNo--;
                return lineNo;
            }

            let parts = line.split("|");
            let newText = parts[0].trim();  // ข้อความ ณ บรรทัดปัจจุบัน
            data.message += `<div>${newText}</div>`; // ใส่ tag div ให้ข้อความ

            // ถ้ามีรูปภาพ หรือ content อื่นๆ
            if (parts.length > 1) {
                // Pop content ตัวแรกสุดออกจาก parts, loop ทีละตัว จนหมด
                for (let path of parts.slice(1)) {
                    let p = path.trim();

                    // Image
                    if (p.startsWith("i=")) {
                        p = p.replace("i=", "");

                        let imageAbsolutePath = "";

                        if (p.includes("<svg")) {
                            imageAbsolutePath = p;
                        }
                        else {
                            imageAbsolutePath = `&nbsp;<image className='quiz-image' src='/images/${quizFile}/${p}'/></images>`;
                        }
                        data.images.push(imageAbsolutePath);
                        data.message += `${imageAbsolutePath}`;
                    }

                    // Mind Map
                    else if (p.startsWith("m=")) {
                        p = p.replace("m=", "");
                        data.mindMap += p;
                    }

                    // VDO
                    else if (p.startsWith("v=")) {
                        p = p.replace("v=", "");
                        data.vdo += p;
                    }

                    // Text
                    else if (p.startsWith("t=")) {
                        p = p.replace("t=", "");
                        data.message += `<div>${p}</div>`;
                    }
                }
            }// end if ถ้ามีรูปภาพ หรือ content อื่นๆ

            lineNo++;
        } // end while

        return lineNo;
    }

    function parseQuizData(text) {
        const lines = text.split("\n");
        const data = [];
        let currentItem = null;
        let questionNo = 0;

        for (let i = 0; i < lines.length; i++) { // i = lineNo started from 0
            const line = lines[i].trim();
            // Every question starts with "--q--"
            if (line === "--q--") {
                if (currentItem) {
                    data.push(currentItem);
                }
                questionNo++;
                currentItem = {
                    no: questionNo, // started from 1
                    question: { text: "", images: [], message: "" },
                    options: [],
                    correct: "",
                    explained: { text: "", images: [], message: "", mindMap: "", vdo: "" },
                    score: 0
                };
                i = buildTag(currentItem.question, i + 1, lines); // i + 1 คือบรรทัดที่อยู่ถัดจาก --q--

                log.info(`${JSON.stringify(currentItem, null, 2)}`)
                // เก็บเลขที่คำถาม map กับ index ของคำถาม
                const quizNumber = currentItem.question.message.match(/\[(\d+)\]/);
                if (quizNumber) {
                    log.info(`${currentItem.question.message}, number: ${quizNumber[1]}`)
                    quizNo[quizNumber[1]] = questionNo - 1;
                    // quizNo[quizNumber[1]] = questionNo;
                }
            } else if (line === "--o--") {
                currentItem.options = [];
                while (lines[i + 1] && lines[i + 1].trim() !== "--a--") {
                    let line = lines[i + 1].trim();
                    // line = "ตัวเลือกที่หนึ่ง|i=question1_option1_img1.png|i=question1_option1_img2.png"
                    let parts = line.split("|");
                    // parts[0] = "ตัวเลือกที่หนึ่ง"
                    // parts[1] = "i=question1_option1_img1.png"
                    // parts[2] = "i=question1_option1_img2.png"

                    /**
                     * Initialize option object
                     *  Json option format:
                     * {
                     *  option: "ตัวเลือกที่หนึ่ง",
                     *  images: [
                     *      "&nbsp;<image src='/images/quiz1/question1_option1_img1.png'/></images>", 
                     *      "&nbsp;<image src='/images/quiz1/question1_option1_img2.png'/></images>"
                     * ],    
                     *  message: "&nbsp;<image src='/images/quiz1/question1_option1_img1.png'/></images>&nbsp;<image src='/images/quiz1/question1_option1_img2.png'/></images>"
                     * }
                    **/
                    let option = {
                        option: parts[0].trim(),
                        images: [],
                        message: ""
                    };

                    // ถ้ามีรูปภาพ
                    if (parts.length > 1) {
                        // images ทั้งหมดหลังจาก ตัวเลือกที่หนึ่ง
                        for (let path of parts.slice(1)) {
                            let p = path.trim();
                            if (path.startsWith("i=")) {
                                p = p.replace("i=", "");
                                let imageAbsolutePath = "";
                                if (p.includes("<svg")) {
                                    imageAbsolutePath = p;
                                }
                                else {
                                    imageAbsolutePath = `&nbsp;<image src='/images/${quizFile}/${p}'/></images>`;
                                }
                                option.message += `${imageAbsolutePath}`;
                                option.images.push(imageAbsolutePath);
                            }

                            else if(p.startsWith("t=")) {
                                option.message += `${p.replace("t=", "")}`;
                            }
                        }
                    }
                    
                    currentItem.options.push(option);
                    i++;
                }
            } else if (line === "--a--") {
                currentItem.correct = lines[i + 1]?.trim();
                i++;
            } else if (line === "--e--") {
                i = buildTag(currentItem.explained, i + 1, lines);
            }
        }// end for, read all lines
        if (currentItem) data.push(currentItem);

        if (wrongQuestionDict) {
            return data.filter(d => wrongQuestionDict[d.no] != null);
        }

        return data;
    }

    // ----- Quiz Game Logic -----
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [result, setResult] = useState("");
    const answerButtonRefs = useRef([]);

    const clearButtonColor = () => {
        if (answerButtonRefs?.current) {
            answerButtonRefs.current.forEach((button) => {
                if (button?.style) {
                    button.style.backgroundColor = "white";
                    button.style.color = "black";
                }
            });
        }
    };

    const handleAnswerClick = (index, option) => {
        let answer = option.option;
        setSelectedAnswer(answer);

        // clear all button color
        quizData[currentQuestion].options.forEach((option, i) => {
            answerButtonRefs.current[i].style.backgroundColor = "white";
            answerButtonRefs.current[i].style.color = "black";
        });

        // Change button color
        answerButtonRefs.current[index].style.color = "white";

        if (answer === quizData[currentQuestion].correct) {
            quizData[currentQuestion].score = 1;
            answerButtonRefs.current[index].style.backgroundColor = "green";
            setResult("✅ คำตอบถูกต้อง!");
        } else {
            quizData[currentQuestion].score = 0;
            answerButtonRefs.current[index].style.backgroundColor = "red";
            setResult("❌ คำตอบผิด!");
        }

        if (quizMode == "TEST") {
            answerButtonRefs.current[index].style.backgroundColor = "blue";
        }
    };

    const handleNextQuestion = () => {
        if (currentQuestion < quizData.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
            setSelectedAnswer(null);
            setResult("");

            clearButtonColor(); // clear all button color
            document.querySelector('input[placeholder="ไปยังคำถามที่..."]').value = ""; // clear input field
        }
    };

    const handlePreviousQuestion = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(currentQuestion - 1);
            setSelectedAnswer(null);
            setResult("");

            clearButtonColor(); // clear all button color
            document.querySelector('input[placeholder="ไปยังคำถามที่..."]').value = ""; // clear input field
        }
    };

    const handleDownloadResult = () => {
        let totalScore = quizData.reduce((total, item) => total + item.score, 0);
        const downloadTime = moment().format("YYYY-MM-DD_HH-mm-ss"); // Example output: "2025-02-05 10:30:45"
        const downloadFile = `${quizFile.replace(".txt", "")}-${downloadTime}.json`

        // quiz result ที่ต้องการบันทึก
        let data = {
            quizFile: quizFile,
            totalScore: totalScore,
            totalQuiz: quizData.length,
            wrong: quizData.filter(q => q.score === 0).map(q => q.no),
            quizTime: downloadTime
        };

        let json = JSON.stringify(data, null, 2); // แปลงเป็น JSON
        const blob = new Blob([json], { type: "application/json" }); // สร้าง Blob
        saveAs(blob, `${downloadFile}`);

        // window.location.href = "/";
    };

    const handleBackToMenu = () => {
        window.location.href = "/";
    };

    return (
        // ต้องเช็คว่า quizData มีข้อมูลหรือไม่ก่อนที่จะแสดงข้อมูล เนื่องจาก React จะทำการ render โค้ดทุกครั้งที่ state มีการเปลี่ยนแปลง
        quizData.length > 0 && (
            <div className="p-6 max-w-md mx-auto bg-white rounded-xl shadow-md space-y-4">

                <PreventPullToRefresh />
                <br />
                {/* Top Menu */}
                <div className="top-menu-row">
                    {/* ปุ่มกลับไปที่เมนู */}
                    <div className="top-menu-col">
                        <button onClick={handleBackToMenu}
                            className="control-label"
                            style={{ marginBottom: "10px", marginLeft: "10px" }}>
                            กลับไปที่เมนู
                        </button>
                    </div>

                    {/* ไปยังคำถามที่ */}
                    <div className="top-menu-col">
                        <input
                            type="text"
                            placeholder="ไปยังคำถามที่..."
                            className="control-label"
                            style={{ marginBottom: "10px", marginLeft: "10px", width: "100px" }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const input = parseInt(e.target.value, 10);
                                    if (input > 0) {
                                        const questionNumber = input - 1;
                                        const questionIndex = quizNo[input];
                                        if (questionIndex != undefined) {
                                            setCurrentQuestion(questionIndex);
                                        }

                                        else {
                                            if (questionNumber < quizData.length) {
                                                setCurrentQuestion(questionNumber);
                                            }
                                            else {
                                                return;
                                            }
                                        }
                                        setSelectedAnswer(null);
                                        setResult("");
                                        clearButtonColor();
                                    }
                                }
                            }}
                        />
                        <button
                            onClick={() => {
                                document.querySelector('input[placeholder="ไปยังคำถามที่..."]').value = "";
                            }}
                            className="control-label"
                            style={{ marginBottom: "10px", marginLeft: "10px" }}
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* คำถาม */}
                <span style={{ fontWeight: "bold", marginTop: "10px", marginBottom: "20px", marginLeft: "10px" }} className="question" dangerouslySetInnerHTML={{ __html: quizData[currentQuestion].question.message }}></span>
                <hr />

                {/* ตัวเลือก */}
                <div className="space-y-2 border p-4 rounded-lg" >
                    {quizData[currentQuestion].options.map((option, index) => (
                        <div className="flex justify-between mt-4 border p-2 rounded-lg"> {/* เว้นบรรทัดระหว่างคำตอบแต่ละข้อ */}
                            <button
                                key={index}
                                ref={(el) => (answerButtonRefs.current[index] = el)}
                                onClick={() => handleAnswerClick(index, option)}
                                className="option-label"
                                style={{ marginBottom: "5px", marginLeft: "10px" }}
                            >
                                {option.option}
                            </button>
                            <span dangerouslySetInnerHTML={{ __html: option.message }} onClick={() => handleAnswerClick(index, option)}></span>
                        </div>
                    ))}
                </div>
                <hr />

                {/* คำตอบ */}
                {result
                    && !(quizMode === "TEST")
                    && (
                        <p className="text-lg font-semibold justify-between mt-4" style={{ marginLeft: "10px" }}>{result}</p>
                    )
                }

                {/* อธิบาย */}
                {selectedAnswer
                    && !(quizMode === "TEST")
                    && result.includes("❌")
                    && (
                        // Explain
                        <div className="space-y-2 border p-4 rounded-lg" >
                            <span className="explain" dangerouslySetInnerHTML={{ __html: quizData[currentQuestion].explained.message }}></span>
                            <div className="top-menu-row">
                                <div className="flex flex-col items-center justify-center min-h-screen top-menu-col" style={{ display: quizData[currentQuestion].explained.mindMap ? "block" : "none", marginLeft: "10px" }}>
                                    <Button variant="contained" onClick={() => setOpenMindMap(true)}>
                                        Mind Map
                                    </Button>
                                    <Modal open={openMindMap} onClose={() => setOpenMindMap(false)}>
                                        <QuizMindMap quizFile={`${quizFile}`} mindMapFile={`${quizData[currentQuestion].explained.mindMap}`} onClose={() => setOpenMindMap(false)}>
                                            <Typography variant="h6">Mind Map</Typography>
                                        </QuizMindMap>
                                    </Modal>
                                </div>
                                <div className="flex flex-col items-center justify-center min-h-screen top-menu-col" style={{ display: quizData[currentQuestion].explained.vdo ? "block" : "none", marginLeft: "10px" }}>
                                    <Button variant="contained" onClick={() => setOpenVdo(true)}>
                                        VDO
                                    </Button>
                                    <Modal open={openVdo} onClose={() => setOpenVdo(false)}>
                                        <QuizVdo quizFile={`${quizFile}`} vdoFile={`${quizData[currentQuestion].explained.vdo}`} onClose={() => setOpenVdo(false)}>
                                            <Typography variant="h6">VDO</Typography>
                                        </QuizVdo>
                                    </Modal>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* ปุ่มคำถามก่อนหน้า, ถัดไป และคะแนนรวม */}
                <div className="flex justify-between mt-4">
                    {currentQuestion > 0 && (
                        <button onClick={handlePreviousQuestion} className="control-label" style={{ marginTop: "10px", marginLeft: "10px" }}>
                            คำถามก่อนหน้า
                        </button>
                    )}
                    {currentQuestion < quizData.length - 1 && (
                        <button onClick={handleNextQuestion} className="control-label" style={{ marginTop: "10px", marginLeft: "10px" }}>
                            คำถามถัดไป
                        </button>
                    )}

                    {/* ถ้าเป็นข้อสุดท้าย */}
                    {currentQuestion == quizData.length - 1
                        && quizMode === "TEST"
                        && (
                            <button onClick={handleDownloadResult} className="control-label" style={{ marginTop: "10px", marginLeft: "10px" }}>
                                Download ผลการทดสอบ
                            </button>
                        )}

                    {/* ถ้าเป็น mode PRACTICE ให้แสดงคะแนนตลอดเวลา */}
                    {quizMode !== "TEST"
                        && (<div className="flex items-center" style={{ marginTop: "10px", marginRight: "10px" }}>
                            <span className="text-lg font-bold" style={{ fontWeight: "bold", color: "red", marginLeft: "10px" }}>
                                คะแนนรวม: {quizData.reduce((total, item) => total + item.score, 0)} / {quizData.length}
                            </span>
                        </div>)
                    }
                </div>
            </div>
        )
    );
}
